import { useState } from "react";
import { Play } from "lucide-react";
import { PhoneFrame } from "./PhoneFrame";
import { getVideoMeta } from "@/data/videos";

interface VideoMeta {
  id: string;
  name: string;
  description?: string;
}

interface YouTubeShortsSectionProps {
  title?: string;
  highlightedTitle?: string;
  /** Simple list of IDs */
  videoIds?: string[];
  /** Rich list with metadata */
  videos?: VideoMeta[];
}

const THUMB_FORMATS = ["maxresdefault.jpg", "hq720.jpg", "sddefault.jpg", "hqdefault.jpg"];

function LazyYouTubeShort({ videoId }: { videoId: string }) {
  const [loaded, setLoaded] = useState(false);
  const [thumbIdx, setThumbIdx] = useState(0);
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/${THUMB_FORMATS[thumbIdx]}`;
  const alt = getVideoMeta(videoId).name;

  return (
    <PhoneFrame>
      {loaded ? (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          title={alt}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 h-full w-full"
        />
      ) : (
        <button
          type="button"
          className="group absolute inset-0 h-full w-full"
          onClick={() => setLoaded(true)}
          aria-label="Play video"
        >
          <img
            src={thumbnailUrl}
            alt={alt}
            className="h-full w-full object-cover"
            loading="lazy"
            onError={() => {
              if (thumbIdx < THUMB_FORMATS.length - 1) setThumbIdx(i => i + 1);
            }}
          />
          <div className="absolute inset-0 bg-background/30 transition-colors duration-300 group-hover:bg-background/10" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary shadow-gold transition-transform duration-300 group-hover:scale-110">
              <Play className="ml-1 h-6 w-6 fill-secondary-foreground text-secondary-foreground" />
            </div>
          </div>
        </button>
      )}
    </PhoneFrame>
  );
}

export function YouTubeShortsSection({ 
  title = "See Our", 
  highlightedTitle = "Work in Action",
  videoIds,
  videos,
}: YouTubeShortsSectionProps) {
  const ids = videos ? videos.map(v => v.id) : (videoIds || []);

  const count = ids.length;
  const gridClass =
    count === 1
      ? "grid grid-cols-1 mx-auto gap-10 max-w-xs"
      : count === 2
        ? "grid grid-cols-1 md:grid-cols-2 mx-auto gap-10 sm:gap-8 max-w-2xl"
        : count === 4
          ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mx-auto gap-10 sm:gap-8 lg:gap-6 max-w-6xl"
          : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mx-auto gap-10 sm:gap-8 max-w-5xl";

  return (
    <section className="py-20 bg-card">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          {title} <span className="text-gradient-gold">{highlightedTitle}</span>
        </h2>
        <div className={gridClass}>
          {ids.map((id) => (
            <LazyYouTubeShort key={id} videoId={id} />
          ))}
        </div>
      </div>
    </section>
  );
}
